import React, { useEffect, useRef } from 'react';
import gql from 'graphql-tag';
import { useForm, FormProvider } from 'react-hook-form';
import { useApolloClient, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { format } from 'date-fns';
import uuid from 'uuid';
import { Icon } from 'semantic-ui-react';

import { Modal, Button } from '../../../components';
import { ClientForm } from './form';
import useUserLookup from '../../../lib/useUserLookup';
import { GET_CLIENTS_QUERY } from './ClientsList';
import { CLIENT_SUMMARY_QUERY } from './ClientsPage';

export const SEARCH_CLIENTS_QUERY = gql`
    query getClients(
        $salon: ID!
        $client: ID!
        $withClient: Boolean!
        $search: String
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    clients(search: $search, first: 30) {
                        nodes {
                            id
                            ...clientFields
                        }
                    }
                    client(id: $client) @include(if: $withClient) {
                        id
                        ...clientFields
                    }
                }
            }
            ... on Master {
                salon(id: $salon) {
                    id
                    clients(search: $search, first: 30) {
                        nodes {
                            id
                            ...clientFields
                        }
                    }
                    client(id: $client) @include(if: $withClient) {
                        id
                        ...clientFields
                    }
                }
            }
        }
    }

    fragment clientFields on Client {
        email
        firstName
        lastName
        phone
        notes
        birthday
        blockedAt
        blockedBy {
            id
            firstName
            lastName
        }
        gender
        type
        deletedAt
    }
`;

export const CREATE_CLIENT_QUERY = gql`
    mutation createClient($input: CreateClientInput!) {
        createClient(input: $input) {
            client {
                id
                email
                firstName
                lastName
                phone
                notes
                birthday
                gender
                type
                blockedAt
                blockedBy {
                    id
                    firstName
                    lastName
                }
            }
        }
    }
`;

export const FIND_CLIENT_BY_PHONE_QUERY = gql`
    query findClientByPhone($phone: String, $role: UserRole) {
        user(phone: $phone, role: $role) {
            id
            ... on Client {
                email
                firstName
                lastName
                phone
                gender
            }
        }
    }
`;

const intlMessages = defineMessages({
    phoneExistingsError: {
        id: 'pages.clients.form.ClientForm.phoneExistingsError',
        defaultMessage: 'Client with the same phone number already exists',
    },
    emailExistingsError: {
        id: 'pages.clients.form.ClientForm.emailExistingsError',
        defaultMessage: 'Client with the same email already exists',
    },
    emailPhoneError: {
        id: 'pages.clients.form.ClientForm.emailPhoneError',
        defaultMessage: 'There are two different users with the provided phone and email',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const DEFAULT_VALUES = {
    firstName: '',
    lastName: '',
    phone: '+371',
    email: '',
    gender: '',
    notes: '',
    birthday: '',
    blocked: false,
};

function updateSummary(store, salon, gender) {
    try {
        const data = store.readQuery({ query: CLIENT_SUMMARY_QUERY, variables: { salon } });
        if (!data?.viewer?.salon?.clients?.summary) return;
        const summary = data.viewer.salon.clients.summary;
        let males = summary.males || 0;
        let females = summary.females || 0;
        if (gender === 'MALE') males++;
        else if (gender === 'FEMALE') females++;
        store.writeQuery({
            query: CLIENT_SUMMARY_QUERY,
            variables: { salon },
            data: {
                ...data,
                viewer: {
                    ...data.viewer,
                    salon: {
                        ...data.viewer.salon,
                        clients: {
                            ...data.viewer.salon.clients,
                            summary: { ...summary, total: (summary.total || 0) + 1, males, females },
                        },
                    },
                },
            },
        });
    } catch (e) {
        // cache may not be populated yet — safe to ignore
    }
}

function CreateClientDialog({ trigger, open, onClose, onAdd, newAppointmentUseCase, intl }) {
    const apolloClient = useApolloClient();
    const salon = useSelector(({ user }) => user.get('salon'));
    const userId = useSelector(({ user }) => user.get('id'));
    const searchQuery = useSelector(({ clients }) => clients.get('searchQuery') || '');
    const { userLookupStatus, userLookupUser, onUserLookup, resetUserLookup } = useUserLookup();
    const prefilledLookupRef = useRef(null);

    const methods = useForm({ defaultValues: DEFAULT_VALUES });
    const {
        handleSubmit,
        reset,
        setError,
        getValues,
        watch,
        trigger: triggerValidation,
        formState: { isSubmitting },
    } = methods;
    const formValues = watch();

    useEffect(() => {
        if (!open) {
            prefilledLookupRef.current = null;
            return;
        }

        if (userLookupStatus !== 'EXISTS' || !userLookupUser) {
            return;
        }

        const lookupKey = userLookupUser.id || [
            userLookupUser.email,
            userLookupUser.firstName,
            userLookupUser.lastName,
            userLookupUser.phone,
            userLookupUser.gender,
        ].join('|');

        if (prefilledLookupRef.current === lookupKey) {
            return;
        }

        prefilledLookupRef.current = lookupKey;
        reset({
            ...getValues(),
            email: userLookupUser.email || '',
            firstName: userLookupUser.firstName || '',
            lastName: userLookupUser.lastName || '',
            phone: userLookupUser.phone || '+371',
            gender: userLookupUser.gender || '',
        });
    }, [open, userLookupStatus, userLookupUser]); // eslint-disable-line react-hooks/exhaustive-deps

    const [createClient] = useMutation(CREATE_CLIENT_QUERY, {
        update: (store, { data: { createClient: result } }) => {
            try {
                const data = store.readQuery({
                    query: GET_CLIENTS_QUERY,
                    variables: { salon, search: searchQuery || undefined, first: 20 },
                });
                if (data?.viewer?.salon?.clients?.edges) {
                    const newClient = {
                        node: result.client,
                        __typename: 'ClientEdge',
                        cursor: btoa(result.client.id),
                    };
                    store.writeQuery({
                        query: GET_CLIENTS_QUERY,
                        variables: { salon, search: searchQuery || undefined, first: 20 },
                        data: {
                            ...data,
                            viewer: {
                                ...data.viewer,
                                salon: {
                                    ...data.viewer.salon,
                                    clients: {
                                        ...data.viewer.salon.clients,
                                        edges: [newClient, ...data.viewer.salon.clients.edges],
                                    },
                                },
                            },
                        },
                    });
                }
            } catch (e) {
                // cache not populated — safe to ignore
            }
            updateSummary(store, salon, result.client.gender);
        },
    });

    const onModalClose = () => {
        prefilledLookupRef.current = null;
        resetUserLookup();
        reset(DEFAULT_VALUES);
        onClose();
    };

    const handleClear = () => {
        prefilledLookupRef.current = null;
        resetUserLookup();
        reset(DEFAULT_VALUES);
    };

    const fetchSearchResults = async (phone) => {
        const { data } = await apolloClient.query({
            query: SEARCH_CLIENTS_QUERY,
            variables: {
                salon,
                client: '',
                withClient: false,
                search: phone,
            },
            fetchPolicy: 'network-only',
        });

        return data?.viewer?.salon?.clients?.nodes?.[0] || null;
    };

    const fetchClientById = async (clientId) => {
        const { data } = await apolloClient.query({
            query: SEARCH_CLIENTS_QUERY,
            variables: {
                salon,
                client: clientId,
                withClient: true,
                search: undefined,
            },
            fetchPolicy: 'network-only',
        });

        return data?.viewer?.salon?.client || null;
    };

    const findExistingAppointmentClient = async (phone) => {
        const clientBySearch = await fetchSearchResults(phone);
        if (clientBySearch) {
            return clientBySearch;
        }

        const { data } = await apolloClient.query({
            query: FIND_CLIENT_BY_PHONE_QUERY,
            variables: {
                phone,
                role: 'CLIENT',
            },
            fetchPolicy: 'network-only',
        });

        const user = data?.user;
        if (!user?.id) {
            return null;
        }

        return (
            (await fetchClientById(user.id)) || {
                __typename: 'Client',
                ...user,
                notes: null,
                birthday: null,
                blockedAt: null,
                blockedBy: null,
                type: 'LOCAL',
                deletedAt: null,
            }
        );
    };

    const checkFormPristine = () => Object.keys(DEFAULT_VALUES).every(key => {
        const value = formValues?.[key];
        return (
            value === DEFAULT_VALUES[key] ||
            (value == null &&
                (DEFAULT_VALUES[key] === '' || DEFAULT_VALUES[key] === false))
        );
    });

    const onSubmit = async formData => {
        const birthday = formData.birthday
            ? format(new Date(formData.birthday), 'MM-dd-yyyy')
            : '';
        const phone = (formData.phone || '').replace(/[^+\d]/g, '');
        const blockedAt = formData.blocked ? format(new Date(), 'yyyy-MM-dd HH:mm') : null;
        const blockedBy = formData.blocked ? userId : null;
        const { blocked, ...rest } = formData;

        try {
            const { data } = await createClient({
                variables: {
                    input: { salon, ...rest, phone, birthday, blockedAt, blockedBy },
                },
                optimisticResponse: {
                    __typename: 'Mutation',
                    createClient: {
                        __typename: 'CreateClientPayload',
                        client: {
                            __typename: 'Client',
                            ...formData,
                            phone,
                            id: uuid.v4(),
                            salon,
                            notes: formData.notes || '',
                            birthday: formData.birthday || '',
                            type: 'LOCAL',
                            blockedAt,
                            blockedBy,
                        },
                    },
                },
            });
            if (onAdd) onAdd(data.createClient.client);
            onModalClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                if (graphQLError.name === 'AlreadyExists') {
                    if (graphQLError.data?.error === 'PHONE_ALREADY_EXISTS') {
                        if (newAppointmentUseCase) {
                            try {
                                const existingClient = await findExistingAppointmentClient(phone);
                                if (existingClient && onAdd) {
                                    onAdd(existingClient);
                                    onModalClose();
                                    return;
                                }
                            } catch (e) {
                                // Fall through to the normal phone conflict error.
                            }
                        }
                        setError('phone', { message: intl.formatMessage(intlMessages.phoneExistingsError) });
                        return;
                    } else if (graphQLError.data?.error === 'EMAIL_ALREADY_EXISTS') {
                        setError('email', { message: intl.formatMessage(intlMessages.emailExistingsError) });
                        return;
                    }
                } else if (graphQLError.name === 'Forbidden') {
                    if (graphQLError.data?.error === 'THERE_ARE_TWO_DIFFERENT_USER_WITH_PROVIDED_PHONE_AND_EMAIL') {
                        setError('root', { message: intl.formatMessage(intlMessages.emailPhoneError) });
                        return;
                    }
                }
                setError('root', { message: graphQLError.message });
                return;
            }
            setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
        }
    };

    const handleAdd = async () => {
        const formData = getValues();
        const phone = (formData.phone || '').replace(/[^+\d]/g, '');

        if (newAppointmentUseCase && phone) {
            const isPhoneValid = await triggerValidation('phone');
            if (!isPhoneValid) {
                return;
            }

            try {
                const existingClient = await findExistingAppointmentClient(phone);
                if (existingClient && onAdd) {
                    onAdd(existingClient);
                    onModalClose();
                    return;
                }
            } catch (e) {
                // Fall through to normal create-client validation/submission.
            }
        }

        await handleSubmit(onSubmit)();
    };

    const readOnly = userLookupStatus === 'EXISTS';

    return (
        <FormProvider {...methods}>
            <Modal trigger={trigger} onClose={onModalClose} open={open}>
                <Modal.Header>
                    <FormattedMessage
                        id="pages.clients.CreateClientDialog.newClient"
                        defaultMessage="New Client"
                    />
                </Modal.Header>
                <Modal.Content>
                    <ClientForm
                        readOnly={readOnly}
                        userLookupStatus={userLookupStatus}
                        userLookupUser={userLookupUser}
                        onUserLookup={onUserLookup}
                        resetUserLookup={resetUserLookup}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleAdd}
                        disabled={isSubmitting || checkFormPristine()}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.clients.CreateClientDialog.add"
                            defaultMessage="Add"
                        />
                    </Button>
                    <Button onClick={handleClear} disabled={isSubmitting} basic>
                        <FormattedMessage
                            id="pages.clients.CreateClientDialog.clear"
                            defaultMessage="Clear"
                        />
                    </Button>
                    <Button onClick={onModalClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.clients.CreateClientDialog.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal>
        </FormProvider>
    );
}

export default injectIntl(CreateClientDialog);
